import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Theme palettes ───────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    mode:        'dark',
    bg:          '#0A0A0A',
    panel:       '#0F0F12',
    card:        '#161618',
    border:      '#242428',
    text:        '#E5E5EA',
    sub:         '#8E8E93',
    dim:         '#48484A',
    meta:        '#0A84FF',
    twilio:      '#25D366',
    telegram:    '#2AABEE',
    web:         '#A78BFA',
    test:        '#F59E0B',
    red:         '#FF453A',
    orange:      '#FF9F0A',
    green:       '#30D158',
    accent:      '#FF2D55',
    // Conversation bubbles
    userBubbleBg:     '#1E1E23',
    botBubbleBg:      '#0B3320',
    botBubbleBorder:  '#1A5C38',
    botBubbleSelBg:   '#0D4A2A',
    botBubbleSelBorder: '#30D158',
    avatarBg:         '#1E1E23',
  },
  light: {
    mode:        'light',
    bg:          '#F7F7F9',
    panel:       '#FFFFFF',
    card:        '#FFFFFF',
    border:      '#E0E0E5',
    text:        '#1C1C1E',
    sub:         '#6E6E73',
    dim:         '#A6A6AB',
    meta:        '#0A84FF',
    twilio:      '#1AAE54',
    telegram:    '#2AABEE',
    web:         '#7B5BE0',
    test:        '#D97706',
    red:         '#D70015',
    orange:      '#C25E00',
    green:       '#1AAE54',
    accent:      '#E0125B',
    userBubbleBg:     '#ECECEF',
    botBubbleBg:      '#D7F2DD',
    botBubbleBorder:  '#7DCB94',
    botBubbleSelBg:   '#B0E5C0',
    botBubbleSelBorder: '#1AAE54',
    avatarBg:         '#ECECEF',
  },
};

// Module-level mutable reference to current theme. Components read from `C`
// (a Proxy that resolves to this object's keys). Toggling the mode at the
// top-level component swaps this reference and triggers a re-render cascade.
let _CURRENT_THEME = THEMES.dark;
const C = new Proxy({}, { get: (_t, k) => _CURRENT_THEME[k] });

function _setThemeMode(mode) {
  _CURRENT_THEME = THEMES[mode] || THEMES.dark;
  if (typeof document !== 'undefined') {
    document.body.style.background = _CURRENT_THEME.bg;
    document.body.style.color = _CURRENT_THEME.text;
  }
}
// Initialise from localStorage once on module load
if (typeof window !== 'undefined') {
  const saved = window.localStorage.getItem('admin_theme_mode');
  if (saved === 'light' || saved === 'dark') _setThemeMode(saved);
}

const CHANNEL_COLOR_KEYS = { meta: 'meta', twilio: 'twilio', telegram: 'telegram', web: 'web', test: 'test', whatsapp: 'twilio' };
const CHANNEL_LABEL = { meta: 'Meta', twilio: 'Twilio', telegram: 'Telegram', web: 'Web', test: 'Test', whatsapp: 'WhatsApp' };

// ─── Helpers ──────────────────────────────────────────────────────────────────
function relTime(ts) {
  if (!ts) return '';
  const diff = (Date.now() - new Date(ts)) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtMs(ms) {
  if (!ms) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

function fmtUSD(n) {
  return `$${(n || 0).toFixed(4)}`;
}

function fmtINR(n) {
  return `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
}

// ─── Chip component ───────────────────────────────────────────────────────────
function Chip({ label, active, onClick, color }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
        background: active ? (color || C.accent) : 'transparent',
        color: active ? '#fff' : C.sub,
        border: `1px solid ${active ? (color || C.accent) : C.border}`,
        transition: 'all 0.15s',
      }}
    >{label}</button>
  );
}

// ─── Channel badge ────────────────────────────────────────────────────────────
function ChannelBadge({ ch }) {
  const c = C[CHANNEL_COLOR_KEYS[ch]] || C.dim;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
      background: `${c}22`, color: c, border: `1px solid ${c}44`,
      textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>{CHANNEL_LABEL[ch] || ch || '?'}</span>
  );
}

// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
function LoginScreen({ onAuth }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(`${API}/admin/businesses`, {
        headers: { Authorization: `Bearer ${pw}` },
      });
      if (res.ok) {
        sessionStorage.setItem('admin_token', pw);
        onAuth(pw);
      } else {
        setErr('Invalid password. Try again.');
      }
    } catch {
      setErr('Connection error. Is the backend running?');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-screen" style={{
      minHeight: '100vh', background: C.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
    }}>
      <div style={{
        width: 360, background: C.panel, border: `1px solid ${C.border}`,
        borderRadius: 20, padding: '40px 32px',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.04em', color: C.text }}>
            NOTE<span style={{ background: 'linear-gradient(135deg,#FF2D55,#5856D6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>HO</span>
          </div>
          <div style={{ fontSize: 13, color: C.sub, marginTop: 6 }}>Operator Dashboard</div>
        </div>
        <form onSubmit={handleSubmit}>
          <input
            data-testid="admin-password-input"
            type="password"
            placeholder="Admin password"
            value={pw}
            onChange={e => { setPw(e.target.value); setErr(''); }}
            autoFocus
            style={{
              width: '100%', padding: '12px 16px', borderRadius: 12, fontSize: 15,
              background: C.card, border: `1px solid ${err ? C.red : C.border}`,
              color: C.text, outline: 'none', boxSizing: 'border-box',
            }}
          />
          {err && <div style={{ color: C.red, fontSize: 12, marginTop: 8 }}>{err}</div>}
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading || !pw}
            style={{
              width: '100%', marginTop: 16, padding: '12px', borderRadius: 12, fontSize: 15,
              fontWeight: 700, cursor: loading || !pw ? 'not-allowed' : 'pointer',
              background: 'linear-gradient(135deg,#FF2D55,#5856D6)', color: '#fff',
              border: 'none', opacity: loading || !pw ? 0.6 : 1, transition: 'opacity 0.2s',
            }}
          >{loading ? 'Verifying…' : 'Sign In'}</button>
        </form>
      </div>
    </div>
  );
}

// ─── LEFT PANEL: User list ────────────────────────────────────────────────────
function UserListPanel({ adminToken, businesses, onSelectUser, selectedUserId }) {
  const [bizFilter, setBizFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (bizFilter) params.set('business_id', bizFilter);
      if (channelFilter) params.set('channel', channelFilter);
      if (errorsOnly) params.set('errors_only', 'true');
      const res = await fetch(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data = await res.json();
      setUsers(data.users || []);
    } catch { setUsers([]); } finally { setLoading(false); }
  }, [adminToken, bizFilter, channelFilter, errorsOnly]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.phone || '').includes(q) ||
      (u.last_message_preview || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  return (
    <div data-testid="user-list-panel" style={{
      width: 270, flexShrink: 0, display: 'flex', flexDirection: 'column',
      borderRight: `1px solid ${C.border}`, background: C.panel, height: '100%',
    }}>
      {/* Business filter */}
      <div style={{ padding: '12px 12px 8px', borderBottom: `1px solid ${C.border}` }}>
        <select
          data-testid="business-filter-dropdown"
          value={bizFilter}
          onChange={e => setBizFilter(e.target.value)}
          style={{
            width: '100%', padding: '7px 10px', borderRadius: 8, fontSize: 12,
            background: C.card, border: `1px solid ${C.border}`, color: C.text, outline: 'none',
          }}
        >
          <option value="">All Businesses</option>
          {businesses.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        {/* Channel chips */}
        <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
          {['', 'meta', 'twilio', 'telegram'].map(ch => (
            <Chip key={ch} label={ch || 'All'} active={channelFilter === ch}
              onClick={() => setChannelFilter(ch)} color={ch ? C[CHANNEL_COLOR_KEYS[ch]] : C.accent} />
          ))}
        </div>
        {/* Error toggle + search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <button
            data-testid="errors-only-toggle"
            onClick={() => setErrorsOnly(p => !p)}
            style={{
              padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: errorsOnly ? `${C.red}22` : 'transparent',
              color: errorsOnly ? C.red : C.sub,
              border: `1px solid ${errorsOnly ? C.red : C.border}`,
              cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}
          >⚠ Errors</button>
        </div>
        <input
          placeholder="Search users…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: '100%', marginTop: 8, padding: '6px 10px', borderRadius: 8, fontSize: 12,
            background: C.card, border: `1px solid ${C.border}`, color: C.text,
            outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>
      {/* User rows */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: C.dim, fontSize: 12 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: C.dim, fontSize: 12 }}>No users found</div>
        ) : filtered.map(u => (
          <div
            key={u.user_id}
            data-testid={`user-row-${u.user_id}`}
            onClick={() => onSelectUser(u)}
            style={{
              padding: '10px 12px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`,
              background: selectedUserId === u.user_id ? `${C.accent}11` : 'transparent',
              borderLeft: selectedUserId === u.user_id ? `2px solid ${C.accent}` : '2px solid transparent',
              transition: 'background 0.1s',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {(u.has_error || u.has_fallback) && (
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.red, display: 'inline-block', flexShrink: 0 }} />
                )}
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>
                  {u.name || u.phone_masked}
                </span>
              </div>
              <ChannelBadge ch={u.channel} />
            </div>
            <div style={{ fontSize: 11, color: C.sub, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {u.business_name && <span style={{ color: C.dim }}>{u.business_name} · </span>}
              {u.phone_masked}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, color: C.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>
                {u.last_message_preview || '—'}
              </span>
              <span style={{ fontSize: 10, color: C.dim, flexShrink: 0, marginLeft: 4 }}>
                {relTime(u.last_message_time)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CENTER PANEL: Conversation view ─────────────────────────────────────────
function ConversationPanel({ conversations, selectedUser, selectedMsgId, onSelectMsg }) {
  const bottomRef = useRef(null);
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [conversations]);

  if (!selectedUser) {
    return (
      <div data-testid="conversation-panel" style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: C.bg, borderRight: `1px solid ${C.border}`,
      }}>
        <div style={{ textAlign: 'center', color: C.dim }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
          <div style={{ fontSize: 14 }}>Select a user to view conversation</div>
        </div>
      </div>
    );
  }

  // Reverse so oldest messages appear at top (API returns newest first)
  const msgs = [...conversations].reverse();

  return (
    <div data-testid="conversation-panel" style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      background: C.bg, borderRight: `1px solid ${C.border}`,
    }}>
      {/* Conversation header */}
      <div style={{
        padding: '10px 16px', borderBottom: `1px solid ${C.border}`,
        background: C.panel, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%', background: C.avatarBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700, color: C.sub, flexShrink: 0,
        }}>{(selectedUser.name || '?')[0].toUpperCase()}</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{selectedUser.name || 'Unknown'}</div>
          <div style={{ fontSize: 11, color: C.sub }}>{selectedUser.phone_masked} · <ChannelBadge ch={selectedUser.channel} /></div>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: C.dim }}>{conversations.length} messages</div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {msgs.map((conv, idx) => (
          <div key={conv.id || idx}>
            {/* Timestamp divider */}
            <div style={{ textAlign: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 10, color: C.dim, background: C.bg, padding: '2px 8px' }}>
                {conv.created_at ? new Date(conv.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                {conv.is_voice_note && ' 🎙'}
              </span>
            </div>
            {/* User message — left, with source badge */}
            {conv.original_content && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 4 }}>
                <div data-testid={`user-msg-${conv.id}`} style={{
                  maxWidth: '72%', padding: '8px 12px', borderRadius: '12px 12px 12px 2px',
                  background: conv.input_type === 'voice' ? '#2d1b4d' : C.userBubbleBg,
                  border: conv.input_type === 'voice' ? '1px solid #a855f730' : 'none',
                  color: C.text, fontSize: 13, lineHeight: 1.5,
                }}>
                  {/* Voice bubble: 🎙 icon + transcript text + "Voice → Text" label */}
                  {conv.input_type === 'voice' || conv.is_voice_note ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 14 }}>🎙</span>
                        <span style={{
                          fontSize: 9, color: '#a855f7', background: '#a855f720',
                          padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                          letterSpacing: 0.3, textTransform: 'uppercase',
                        }}>Voice → Text</span>
                      </div>
                      <div style={{ fontStyle: 'italic', color: '#e9d5ff' }}>
                        {conv.media_transcription || conv.voice_transcription || conv.original_content}
                      </div>
                    </>
                  ) : conv.input_type && conv.input_type !== 'text' ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 14 }}>
                          {conv.input_type === 'image'    ? '📷' :
                           conv.input_type === 'document' ? '📄' :
                           conv.input_type === 'video'    ? '🎬' :
                           conv.input_type === 'sticker'  ? '🏷' :
                           conv.input_type === 'reaction' ? '😊' :
                           conv.input_type === 'location' ? '📍' :
                           conv.input_type === 'audio'    ? '🔊' : '💬'}
                        </span>
                        <span style={{
                          fontSize: 9, color: C.sub, background: '#1a1a1a',
                          padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                          letterSpacing: 0.3, textTransform: 'uppercase',
                        }}>{conv.input_type}</span>
                      </div>
                      <div>{conv.original_content}</div>
                    </>
                  ) : (
                    <>{conv.original_content}</>
                  )}
                  {conv.detected_language && conv.detected_language !== 'english' && (
                    <span style={{ fontSize: 10, color: C.sub, marginLeft: 6 }}>[{conv.detected_language}]</span>
                  )}
                  {/* Source badge on the inbound bubble itself */}
                  {(conv.channel || selectedUser.channel) && (
                    <div style={{ marginTop: 4 }}>
                      <ChannelBadge ch={conv.channel || selectedUser.channel} />
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Bot response — right, clickable */}
            {conv.sonnet_response && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
                <div
                  data-testid={`bot-msg-${conv.id}`}
                  onClick={() => onSelectMsg(conv)}
                  style={{
                    maxWidth: '72%', padding: '8px 12px', borderRadius: '12px 12px 2px 12px',
                    background: selectedMsgId === conv.id ? C.botBubbleSelBg : C.botBubbleBg,
                    border: `1px solid ${selectedMsgId === conv.id ? C.botBubbleSelBorder : C.botBubbleBorder}`,
                    color: C.text, fontSize: 13, lineHeight: 1.5,
                    cursor: 'pointer', transition: 'background 0.15s',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {conv.sonnet_response}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    {conv.agent_routed_name && (
                      <span style={{ fontSize: 10, color: C.green, background: `${C.green}20`, padding: '1px 5px', borderRadius: 4 }}>
                        AI · {conv.agent_routed_name}
                      </span>
                    )}
                    {conv.meta_message_id && (
                      <span style={{ fontSize: 10, color: C.dim }}>#{conv.meta_message_id.slice(-8)}</span>
                    )}
                    {(conv.model_fallback || conv.router_fallback) && (
                      <span style={{ fontSize: 10, color: C.orange, background: `${C.orange}20`, padding: '1px 5px', borderRadius: 4 }}>⚠ Fallback</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Timing marker */}
            {conv.response_time_total_ms > 0 && (
              <div style={{ textAlign: 'right', marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: conv.response_time_total_ms > 10000 ? C.orange : C.dim }}>
                  ⏱ {fmtMs(conv.response_time_total_ms)}
                </span>
              </div>
            )}
            {/* Error */}
            {conv.error_message && (
              <div style={{
                margin: '4px 0', padding: '6px 10px', borderRadius: 8,
                background: `${C.red}15`, border: `1px solid ${C.red}44`,
                color: C.red, fontSize: 11,
              }}>⚠ {conv.error_message}</div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

// ─── RIGHT PANEL: Message intelligence ────────────────────────────────────────
function IntelligencePanel({ selectedUser, userStats, selectedMsg, rules, workers, costSettings }) {
  if (!selectedUser) {
    return (
      <div data-testid="intelligence-panel" style={{
        width: 310, flexShrink: 0, background: C.panel,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ color: C.dim, fontSize: 12, textAlign: 'center', padding: 20 }}>
          Select a user to view details
        </div>
      </div>
    );
  }

  return (
    <div data-testid="intelligence-panel" style={{
      width: 310, flexShrink: 0, background: C.panel, overflowY: 'auto',
      borderLeft: `1px solid ${C.border}`,
    }}>
      {!selectedMsg ? (
        // User summary
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
            User Summary
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              ['Name', selectedUser.name || '—'],
              ['Business', selectedUser.business_name || '—'],
              ['Role', selectedUser.role || '—'],
              ['Channel', <ChannelBadge ch={selectedUser.channel} />],
              ['Total Messages', userStats?.total_messages || 0],
              ['Entries Saved', userStats?.total_entries || 0],
              ['Avg Latency', fmtMs(userStats?.avg_latency_ms)],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: C.sub }}>{label}</span>
                <span style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>{val}</span>
              </div>
            ))}
          </div>

          {rules.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Active Business Rules
              </div>
              {rules.map((r, i) => (
                <div key={i} style={{
                  padding: '6px 10px', borderRadius: 8, background: C.card,
                  border: `1px solid ${C.border}`, marginBottom: 4, fontSize: 11, color: C.text,
                }}>
                  {r.rule_name}: <strong>{r.value} {r.unit}</strong>
                </div>
              ))}
            </div>
          )}

          {workers.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Registered Workers ({workers.length})
              </div>
              {workers.slice(0, 5).map((w, i) => (
                <div key={i} style={{
                  padding: '5px 10px', borderRadius: 8, background: C.card,
                  border: `1px solid ${C.border}`, marginBottom: 4, fontSize: 11,
                  display: 'flex', justifyContent: 'space-between',
                }}>
                  <span style={{ color: C.text }}>{w.name}</span>
                  <span style={{ color: C.dim }}>{w.role}</span>
                </div>
              ))}
              {workers.length > 5 && <div style={{ fontSize: 11, color: C.dim, textAlign: 'center' }}>+{workers.length - 5} more</div>}
            </div>
          )}
        </div>
      ) : (
        // Message debug card
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
            Message Intelligence
          </div>

          {/* Source */}
          <Section title="Source">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <ChannelBadge ch={selectedMsg.channel || selectedUser.channel} />
              <span style={{ fontSize: 11, color: C.sub }}>
                {selectedMsg.direction === 'outbound' ? 'outbound' : 'inbound'}
              </span>
            </div>
          </Section>

          {/* Input Type / Media — show for any non-text input */}
          {(selectedMsg.input_type && selectedMsg.input_type !== 'text') || selectedMsg.is_voice_note || selectedMsg.media_transcription ? (
            <Section title={selectedMsg.input_type === 'voice' || selectedMsg.is_voice_note ? 'Voice Transcription' : 'Media Input'}>
              <Row label="Input type" val={selectedMsg.input_type || (selectedMsg.is_voice_note ? 'voice' : 'text')} mono />
              {selectedMsg.media_id && (
                <Row label="Media ID" val={String(selectedMsg.media_id).slice(0, 24) + (String(selectedMsg.media_id).length > 24 ? '…' : '')} mono />
              )}
              {(selectedMsg.media_transcription || selectedMsg.voice_transcription) && (
                <div style={{ marginTop: 8, padding: '8px 10px', background: '#1e0a3b', border: '1px solid #a855f730', borderRadius: 6 }}
                     data-testid="voice-transcription-section">
                  <div style={{ fontSize: 9, color: '#a855f7', fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase', marginBottom: 4 }}>
                    🎙 Whisper-1 Transcript
                  </div>
                  <div style={{ fontSize: 12, color: '#e9d5ff', fontStyle: 'italic', lineHeight: 1.5 }}>
                    {selectedMsg.media_transcription || selectedMsg.voice_transcription}
                  </div>
                </div>
              )}
            </Section>
          ) : null}

          {/* Agent info — full multi-agent routing */}
          <Section title="Agent Routing">
            <Row label="Final agent" val={selectedMsg.agent_routed_name || '—'} />
            <Row label="Agent ID" val={selectedMsg.agent_routed_final || '—'} mono />
            <Row label="Routing method" val={selectedMsg.routing_method || '—'} mono />
            <Row label="Confidence" val={selectedMsg.haiku_confidence ? `${Math.round(selectedMsg.haiku_confidence * 100)}%` : '—'} />
            <Row label="Gate triggered" val={selectedMsg.confidence_gate_triggered ? 'Yes' : 'No'}
              color={selectedMsg.confidence_gate_triggered ? C.orange : C.green} />
            {Array.isArray(selectedMsg.agents_considered) && selectedMsg.agents_considered.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <div style={{ fontSize: 10, color: C.dim, marginBottom: 4 }}>
                  All agents considered ({selectedMsg.agents_considered.length})
                </div>
                {selectedMsg.agents_considered.map((a, i) => {
                  const isWinner = a.agent_id === selectedMsg.agent_routed_final;
                  return (
                    <div key={i} data-testid={`agent-considered-${a.agent_id}`} style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '4px 8px', borderRadius: 6, marginBottom: 3,
                      background: isWinner ? `${C.green}15` : C.card,
                      border: `1px solid ${isWinner ? `${C.green}44` : C.border}`,
                      fontSize: 11,
                    }}>
                      <span style={{ color: isWinner ? C.green : C.text, fontWeight: isWinner ? 700 : 500, flex: 1 }}>
                        {a.agent_name || `Agent ${a.agent_id}`}
                      </span>
                      <span style={{ color: C.sub, fontFamily: 'monospace' }}>#{a.agent_id}</span>
                      {a.confidence != null && (
                        <span style={{ color: isWinner ? C.green : C.sub, fontWeight: 600, minWidth: 38, textAlign: 'right' }}>
                          {Math.round(a.confidence * 100)}%
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Timing breakdown */}
          <Section title="Latency">
            <Row label="Router" val={fmtMs(selectedMsg.response_time_haiku_ms)} />
            <Row label="Generator" val={fmtMs(selectedMsg.response_time_sonnet_ms)} />
            <Row label="Total" val={fmtMs(selectedMsg.response_time_total_ms)}
              color={selectedMsg.response_time_total_ms > 10000 ? C.orange : C.green} />
            {selectedMsg.validator_ms > 0 && <Row label="Validator" val={fmtMs(selectedMsg.validator_ms)} />}
            {selectedMsg.timing_gap_ms !== undefined && <Row label="Gap" val={`${selectedMsg.timing_gap_ms}ms`}
              color={selectedMsg.timing_gap_ms > 10 ? C.orange : C.green} />}
          </Section>

          {/* Models */}
          {(selectedMsg.model_used || selectedMsg.router_model) && (
            <Section title="Models Used">
              {selectedMsg.router_model && <Row label="Router" val={selectedMsg.router_model} mono />}
              {selectedMsg.model_used && <Row label="Generator" val={selectedMsg.model_used} mono />}
            </Section>
          )}

          {/* Tokens */}
          <TokensSection msg={selectedMsg} />

          {/* Cost — uses live Redis cost settings */}
          <CostSection msg={selectedMsg} costSettings={costSettings} />

          {/* Fallback status */}
          <Section title="Fallback Status">
            <div style={{
              padding: '8px 10px', borderRadius: 8,
              background: (selectedMsg.model_fallback || selectedMsg.router_fallback) ? `${C.red}15` : `${C.green}15`,
              border: `1px solid ${(selectedMsg.model_fallback || selectedMsg.router_fallback) ? `${C.red}44` : `${C.green}44`}`,
              fontSize: 12, color: (selectedMsg.model_fallback || selectedMsg.router_fallback) ? C.red : C.green,
              fontWeight: 600,
            }}>
              {(selectedMsg.model_fallback || selectedMsg.router_fallback) ? '⚠ Fallback Triggered' : '✓ No Fallback'}
            </div>
            {selectedMsg.model_fallback && selectedMsg.model_fallback_reason && (
              <Row label="Gen reason" val={selectedMsg.model_fallback_reason} color={C.red} />
            )}
            {selectedMsg.router_fallback && selectedMsg.router_fallback_reason && (
              <Row label="Route reason" val={selectedMsg.router_fallback_reason} color={C.orange} />
            )}
          </Section>

          {/* Validator corrections — PROMINENT */}
          <Section title="Validator Corrections" highlight>
            {selectedMsg.validator_ms > 0 ? (
              <>
                <Row label="Validator ran" val={`${selectedMsg.validator_ms}ms`} color={C.twilio} />
                {selectedMsg.validator_corrections && selectedMsg.validator_corrections.length > 0 ? (
                  selectedMsg.validator_corrections.map((vc, i) => (
                    <div key={i} style={{
                      marginTop: 6, padding: '6px 8px', borderRadius: 8,
                      background: `${C.orange}15`, border: `1px solid ${C.orange}44`,
                      fontSize: 11,
                    }}>
                      <div style={{ color: C.sub, marginBottom: 2 }}>{vc.field}</div>
                      <div style={{ color: C.red }}>Before: <span style={{ fontFamily: 'monospace' }}>{JSON.stringify(vc.before)}</span></div>
                      <div style={{ color: C.green }}>After: <span style={{ fontFamily: 'monospace' }}>{JSON.stringify(vc.after)}</span></div>
                      {vc.reason && <div style={{ color: C.dim, marginTop: 2 }}>Reason: {vc.reason}</div>}
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>No field corrections recorded</div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 11, color: C.dim }}>Validator not triggered (template path or single-agent)</div>
            )}
          </Section>

          {/* Entry + Meta */}
          {(selectedMsg.entry_id || selectedMsg.meta_message_id) && (
            <Section title="References">
              {selectedMsg.entry_id && <Row label="Entry ID" val={selectedMsg.entry_id.slice(0, 8) + '…'} mono />}
              {selectedMsg.meta_message_id && <Row label="Meta Msg ID" val={selectedMsg.meta_message_id} mono small />}
            </Section>
          )}

          {/* Full response */}
          <Section title="Full Response">
            <div style={{
              fontSize: 11, color: C.text, background: C.card,
              border: `1px solid ${C.border}`, borderRadius: 8, padding: 10,
              maxHeight: 120, overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.5,
            }}>{selectedMsg.sonnet_response || '—'}</div>
          </Section>
        </div>
      )}
    </div>
  );
}

// ─── Section + Row helpers ────────────────────────────────────────────────────
function Section({ title, children, highlight }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: highlight ? C.orange : C.sub,
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
        ...(highlight ? { padding: '2px 0', borderBottom: `1px solid ${C.orange}44` } : {}),
      }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
    </div>
  );
}

function Row({ label, val, mono, color, small }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
      <span style={{ fontSize: 11, color: C.sub, flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: small ? 10 : 11, color: color || C.text, fontWeight: 500,
        fontFamily: mono ? 'monospace' : 'inherit',
        textAlign: 'right', wordBreak: 'break-all',
      }}>{val}</span>
    </div>
  );
}

// ── Token + cost helpers (front-end mirror of backend _normalise_model_key) ──
const _KNOWN_PRICING_KEYS = [
  'gemini-2.5-flash-lite', 'gemini-2.5-flash', 'gemini-2.5-pro',
  'claude-haiku-3.5', 'claude-sonnet-3.5', 'claude-sonnet-4', 'claude-opus-4',
  'gpt-4o', 'gpt-4o-mini', 'gpt-4.1', 'gpt-4.1-mini', 'o3-mini',
];
function normaliseModelKey(raw) {
  if (!raw) return null;   // caller decides fallback (may use router_model)
  const s = String(raw).trim().toLowerCase();
  // Explicit full-ID aliases (covers all model IDs the pipeline writes to DB)
  const aliases = {
    // Gemini
    'gemini-2.5-flash-lite-preview':  'gemini-2.5-flash-lite',
    'gemini-flash-lite':              'gemini-2.5-flash-lite',
    'gemini-3.1-flash-lite-preview':  'gemini-2.5-flash-lite',
    'gemini-flash':                   'gemini-2.5-flash',
    'gemini-pro':                     'gemini-2.5-pro',
    // Claude — full versioned IDs
    'claude-haiku-4-5-20251001':      'claude-haiku-3.5',
    'claude-haiku':                   'claude-haiku-3.5',
    'claude-sonnet-4-5-20250929':     'claude-sonnet-4',   // ← THE KEY FIX
    'claude-sonnet-3-5-20241022':     'claude-sonnet-3.5',
    'claude-sonnet':                  'claude-sonnet-4',
    'claude-opus':                    'claude-opus-4',
    // Short labels stored in older records
    'sonnet':                         'claude-sonnet-4',
    'haiku':                          'claude-haiku-3.5',
  };
  if (aliases[s]) return aliases[s];
  // Direct match against known pricing keys
  if (_KNOWN_PRICING_KEYS.includes(s)) return s;
  // Substring fallback (same logic as backend _normalise_model_key)
  for (const k of _KNOWN_PRICING_KEYS) {
    if (s.includes(k) || k.includes(s)) return k;
  }
  return s;
}

function TokensSection({ msg }) {
  const inTok  = msg.input_tokens  || 0;
  const outTok = msg.output_tokens || 0;
  const total  = inTok + outTok;
  if (!total && !msg.model_used) return null;
  return (
    <Section title="Tokens">
      <Row label="Input"  val={inTok.toLocaleString()} mono />
      <Row label="Output" val={outTok.toLocaleString()} mono />
      <Row label="Total"  val={total.toLocaleString()} mono color={C.accent} />
      {msg.model_used && <Row label="Model" val={msg.model_used} mono small />}
    </Section>
  );
}

function CostSection({ msg, costSettings }) {
  const inTok  = msg.input_tokens  || 0;
  const outTok = msg.output_tokens || 0;
  if (!inTok && !outTok) return null;
  const models = (costSettings && costSettings.models_pricing) || {};
  const usdToInr = (costSettings && costSettings.usd_to_inr) || 84;

  // Resolve model key: generator first, then router, then default
  const genKey    = normaliseModelKey(msg.model_used);
  const routerKey = normaliseModelKey(msg.router_model);
  const resolvedKey = (genKey && models[genKey])
    ? genKey
    : (routerKey && models[routerKey])
      ? routerKey
      : 'gemini-2.5-flash-lite';

  const price = models[resolvedKey] || { input_per_m: 0.10, output_per_m: 0.40, label: 'Gemini 2.5 Flash-Lite' };
  const inputCost  = (inTok  / 1_000_000) * (price.input_per_m  || 0);
  const outputCost = (outTok / 1_000_000) * (price.output_per_m || 0);
  const totalUSD = inputCost + outputCost;
  const totalINR = totalUSD * usdToInr;
  return (
    <Section title="Cost">
      <Row label="Input"  val={`$${inputCost.toFixed(6)}`} mono />
      <Row label="Output" val={`$${outputCost.toFixed(6)}`} mono />
      <Row label="Total (USD)" val={`$${totalUSD.toFixed(6)}`} mono color={C.green} />
      <Row label="Total (INR)" val={`₹${totalINR.toFixed(4)}`} mono color={C.green} />
      <Row label="Pricing" val={`${price.label || resolvedKey} · $${price.input_per_m}/M in · $${price.output_per_m}/M out`} small />
    </Section>
  );
}

// ─── CONVERSATION MONITOR PAGE ────────────────────────────────────────────────
function ConversationMonitor({ adminToken, businesses }) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [costSettings, setCostSettings] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch cost settings once so the cost-per-message widget can use the
  // operator's configured per-model pricing and exchange rate.
  useEffect(() => {
    if (!adminToken) return;
    fetch(`${API}/admin/cost-settings`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(r => r.json())
      .then(d => setCostSettings(d.settings || null))
      .catch(() => {});
  }, [adminToken]);

  const handleSelectUser = useCallback(async (user) => {
    setSelectedUser(user);
    setSelectedMsg(null);
    setConversations([]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/conversation/${encodeURIComponent(user.phone)}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data = await res.json();
      setConversations(data.conversations || []);
      setUserStats(data.stats || null);
      setRules(data.rules || []);
      setWorkers(data.workers || []);
    } catch { setConversations([]); } finally { setLoading(false); }
  }, [adminToken]);

  return (
    <div data-testid="conversation-monitor" style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <UserListPanel
        adminToken={adminToken} businesses={businesses}
        onSelectUser={handleSelectUser} selectedUserId={selectedUser?.user_id}
      />
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
          <div style={{ color: C.dim, fontSize: 13 }}>Loading conversation…</div>
        </div>
      ) : (
        <ConversationPanel
          conversations={conversations} selectedUser={selectedUser}
          selectedMsgId={selectedMsg?.id} onSelectMsg={setSelectedMsg}
        />
      )}
      <IntelligencePanel
        selectedUser={selectedUser} userStats={userStats}
        selectedMsg={selectedMsg} rules={rules} workers={workers}
        costSettings={costSettings}
      />
    </div>
  );
}

// ─── AI EXTRACTION QUALITY WIDGET ─────────────────────────────────────────────

// ── Cost-panel helper components ─────────────────────────────────────────────
function PanelSection({ title, color, children }) {
  return (
    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: color || C.sub, textTransform: 'uppercase',
        letterSpacing: '0.06em', marginBottom: 10,
      }}>{title}</div>
      {children}
    </div>
  );
}

function NumberRow({ keyName, label, costForm, setCostForm, step = 'any' }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 11, color: C.sub, display: 'block', marginBottom: 4 }}>{label}</label>
      <input
        data-testid={`cost-input-${keyName}`}
        type="number" step={step}
        value={costForm[keyName] ?? ''}
        onChange={e => setCostForm(p => ({ ...p, [keyName]: parseFloat(e.target.value) || 0 }))}
        style={{
          width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13,
          background: C.card, border: `1px solid ${C.border}`, color: C.text,
          outline: 'none', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

function InfraTotals({ costForm, infra }) {
  const total = (costForm.supabase_fixed_inr || 0) + (costForm.railway_fixed_inr || 0) + (costForm.redis_fixed_inr || 0);
  const perMsg = infra && infra.total_msgs > 0 ? (total / infra.total_msgs).toFixed(2) : '—';
  return (
    <div data-testid="infra-totals" style={{
      marginTop: 6, padding: '10px 12px', borderRadius: 8,
      background: `${C.web}10`, border: `1px solid ${C.web}33`, fontSize: 11,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', color: C.sub }}>
        <span>Total infrastructure / month</span>
        <span style={{ color: C.web, fontWeight: 700 }}>{fmtINR(total)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', color: C.sub, marginTop: 4 }}>
        <span>Per-message ({infra?.total_msgs || 0} msgs this month)</span>
        <span style={{ color: C.text, fontWeight: 600 }}>₹{perMsg}</span>
      </div>
    </div>
  );
}

const PROVIDER_COLORS = { google: '#4285F4', anthropic: '#D97757', openai: '#10A37F' };
const PROVIDER_LABELS = { google: 'Google Gemini', anthropic: 'Anthropic Claude', openai: 'OpenAI GPT' };

function ModelPricingSection({ costForm, setCostForm }) {
  const models = costForm.models_pricing || {};
  // Group by provider
  const byProvider = {};
  Object.entries(models).forEach(([k, v]) => {
    const p = (v && v.provider) || 'other';
    (byProvider[p] = byProvider[p] || []).push([k, v]);
  });
  const updateModel = (modelKey, field, value) => {
    setCostForm(prev => ({
      ...prev,
      models_pricing: {
        ...(prev.models_pricing || {}),
        [modelKey]: {
          ...((prev.models_pricing || {})[modelKey] || {}),
          [field]: parseFloat(value) || 0,
        },
      },
    }));
  };
  return (
    <div data-testid="model-pricing-section">
      <div style={{
        fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase',
        letterSpacing: '0.06em', marginBottom: 10,
      }}>AI Model Pricing ($ / 1M tokens)</div>
      {['google', 'anthropic', 'openai'].map(provider => {
        const list = byProvider[provider] || [];
        if (!list.length) return null;
        const color = PROVIDER_COLORS[provider] || C.sub;
        return (
          <div key={provider} style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 6,
            }}>{PROVIDER_LABELS[provider]}</div>
            <div style={{
              display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr', gap: 6,
              fontSize: 9, color: C.dim, paddingLeft: 4, marginBottom: 4,
            }}>
              <span>Model</span>
              <span style={{ textAlign: 'right' }}>Input $/M</span>
              <span style={{ textAlign: 'right' }}>Output $/M</span>
            </div>
            <div style={{
              borderRadius: 8, border: `1px solid ${color}33`,
              background: `${color}08`, padding: 6,
            }}>
              {list.map(([key, v]) => (
                <div key={key} data-testid={`model-row-${key}`} style={{
                  display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr', gap: 6,
                  alignItems: 'center', padding: '4px 0',
                }}>
                  <span style={{ fontSize: 11, color: C.text, fontWeight: 500, paddingLeft: 4 }}>{v.label || key}</span>
                  <input
                    data-testid={`model-input-${key}`}
                    type="number" step="0.01"
                    value={v.input_per_m ?? ''}
                    onChange={e => updateModel(key, 'input_per_m', e.target.value)}
                    style={{
                      padding: '5px 8px', borderRadius: 6, fontSize: 11,
                      background: C.card, border: `1px solid ${C.border}`, color: C.text,
                      outline: 'none', boxSizing: 'border-box', textAlign: 'right',
                    }}
                  />
                  <input
                    data-testid={`model-output-${key}`}
                    type="number" step="0.01"
                    value={v.output_per_m ?? ''}
                    onChange={e => updateModel(key, 'output_per_m', e.target.value)}
                    style={{
                      padding: '5px 8px', borderRadius: 6, fontSize: 11,
                      background: C.card, border: `1px solid ${C.border}`, color: C.text,
                      outline: 'none', boxSizing: 'border-box', textAlign: 'right',
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ExtractionQualityWidget({ data }) {
  if (!data) return null;
  const { top_fields = [], per_agent = [], total_messages_window = 0, msgs_with_corrections = 0, msgs_corrected_pct = 0 } = data;

  const TrendIcon = ({ t }) => {
    if (t === 'up')   return <span style={{ color: C.red,    fontSize: 14, fontWeight: 700 }}>↑</span>;
    if (t === 'down') return <span style={{ color: C.green,  fontSize: 14, fontWeight: 700 }}>↓</span>;
    return                  <span style={{ color: C.dim,    fontSize: 14, fontWeight: 700 }}>—</span>;
  };

  return (
    <div data-testid="extraction-quality-widget" style={{
      borderRadius: 14, overflow: 'hidden', border: `1px solid ${C.border}`,
      background: C.card, marginBottom: 20,
    }}>
      <div style={{
        padding: '12px 16px', background: C.panel, borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
          AI Extraction Quality
          <span style={{ marginLeft: 8, fontSize: 10, color: C.dim, fontWeight: 500 }}>
            last 7 days · {total_messages_window} msgs · {msgs_with_corrections} corrected ({msgs_corrected_pct}%)
          </span>
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 0 }}>
        {/* Top corrected fields */}
        <div style={{ padding: 16, borderRight: `1px solid ${C.border}` }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase',
            letterSpacing: '0.06em', marginBottom: 10,
          }}>Top corrected fields</div>
          {top_fields.length === 0 ? (
            <div style={{ fontSize: 12, color: C.dim, padding: '12px 0' }}>No corrections in this window.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {top_fields.map((f, i) => (
                <div key={f.field} data-testid={`top-field-${f.field}`} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px',
                  borderRadius: 10, background: C.bg, border: `1px solid ${C.border}`,
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.dim, width: 18 }}>#{i + 1}</span>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'monospace' }}>
                    {f.field}
                  </span>
                  <span style={{ fontSize: 12, color: C.sub }}>
                    {f.count} {f.count === 1 ? 'corr.' : 'corrs.'}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.accent, minWidth: 50, textAlign: 'right' }}>
                    {f.percentage}%
                  </span>
                  <TrendIcon t={f.trend} />
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Per-agent breakdown */}
        <div style={{ padding: 16 }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase',
            letterSpacing: '0.06em', marginBottom: 10,
          }}>Corrections by agent</div>
          {per_agent.length === 0 ? (
            <div style={{ fontSize: 12, color: C.dim, padding: '12px 0' }}>No agent data yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {per_agent.slice(0, 5).map(a => {
                const rateColor = a.correction_rate_pct >= 5 ? C.red
                                : a.correction_rate_pct >= 1 ? C.orange
                                : C.green;
                return (
                  <div key={a.agent_id} data-testid={`agent-corr-${a.agent_id}`} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
                    borderRadius: 8, background: C.bg, border: `1px solid ${C.border}`,
                  }}>
                    <span style={{ flex: 1, fontSize: 12, color: C.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.agent_name}
                    </span>
                    <span style={{ fontSize: 11, color: C.dim, minWidth: 60, textAlign: 'right' }}>
                      {a.corrections}/{a.messages}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: rateColor, minWidth: 50, textAlign: 'right' }}>
                      {a.correction_rate_pct}%
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── USAGE DASHBOARD PAGE ─────────────────────────────────────────────────────
function UsageDashboard({ adminToken, onDrillDown }) {
  const [summary, setSummary] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [costSettings, setCostSettings] = useState(null);
  const [extractionQuality, setExtractionQuality] = useState(null);
  const [showCostPanel, setShowCostPanel] = useState(false);
  const [sortField, setSortField] = useState('messages_received');
  const [sortDir, setSortDir] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [costForm, setCostForm] = useState({});
  const [expandedBiz, setExpandedBiz] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, anomRes, costRes, eqRes] = await Promise.all([
        fetch(`${API}/admin/usage-summary`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        fetch(`${API}/admin/anomalies`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        fetch(`${API}/admin/cost-settings`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        fetch(`${API}/admin/extraction-quality`, { headers: { Authorization: `Bearer ${adminToken}` } }),
      ]);
      const [sumData, anomData, costData, eqData] = await Promise.all([
        sumRes.json(), anomRes.json(), costRes.json(), eqRes.json(),
      ]);
      setSummary(sumData);
      setAnomalies(anomData.anomalies || []);
      const cs = costData.settings || {};
      setCostSettings(cs);
      setCostForm(cs);
      setExtractionQuality(eqData);
    } catch (e) {
      console.error('[UsageDashboard] fetchAll error:', e);
    } finally { setLoading(false); }
  }, [adminToken]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveCostSettings = async () => {
    try {
      await fetch(`${API}/admin/cost-settings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(costForm),
      });
      setCostSettings(costForm);
      setShowCostPanel(false);
      // Re-fetch usage so per-model costs refresh against new pricing
      fetchAll();
    } catch { }
  };

  const sortedBizs = useMemo(() => {
    if (!summary?.businesses) return [];
    return [...summary.businesses].sort((a, b) => {
      const av = a[sortField] ?? 0, bv = b[sortField] ?? 0;
      return sortDir * (typeof av === 'string' ? av.localeCompare(bv) : (av - bv));
    });
  }, [summary, sortField, sortDir]);

  const today = summary?.today || {};
  const projection = summary?.projection || null;
  const infra = summary?.infra || null;

  // Cost calculation per business — uses server-supplied AI + messaging cost
  // (which already applies per-model pricing) and adds the per-business
  // share of fixed infrastructure cost in INR.
  const calcCost = useCallback((biz) => {
    if (!costSettings) return null;
    const cs = costSettings;
    const usdToInr = cs.usd_to_inr || 84;
    const aiCost = biz.ai_cost_usd || 0;
    const msgCost = biz.messaging_cost_usd || 0;
    // Distribute infra ₹ across active businesses only; inactive biz pay nothing
    const activeCount = sortedBizs.filter(b => b.messages_received > 0).length || 1;
    const isActive = (biz.messages_received || 0) > 0;
    const infraPerBizInr = isActive ? (((cs.supabase_fixed_inr || 0) + (cs.railway_fixed_inr || 0) + (cs.redis_fixed_inr || 0)) / activeCount) : 0;
    const totalUSD = aiCost + msgCost + (infraPerBizInr / usdToInr);
    const totalINR = totalUSD * usdToInr;
    const targetINR = cs.target_charge_per_biz_inr || 999;
    return {
      aiCost,
      msgCost,
      infraPerBizInr,
      totalUSD,
      totalINR,
      targetINR,
      margin: targetINR - totalINR,
      perModel: biz.per_model || [],
    };
  }, [costSettings, sortedBizs]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => -d);
    else { setSortField(field); setSortDir(-1); }
  };

  const SortHeader = ({ field, label }) => (
    <th onClick={() => handleSort(field)} style={{
      padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.sub,
      textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', userSelect: 'none',
      background: sortField === field ? `${C.accent}15` : 'transparent', whiteSpace: 'nowrap',
    }}>
      {label} {sortField === field ? (sortDir === -1 ? '↓' : '↑') : ''}
    </th>
  );

  const [showAllAnomalies, setShowAllAnomalies] = useState(false);
  const visibleAnomalies = showAllAnomalies ? anomalies : anomalies.slice(0, 5);

  const SEVERITY_COLOR = { critical: C.red, warning: C.orange };

  if (loading) return (
    <div data-testid="usage-dashboard" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ color: C.dim }}>Loading…</div>
    </div>
  );

  return (
    <div data-testid="usage-dashboard" style={{ flex: 1, overflowY: 'auto', background: C.bg, padding: 20 }}>
      {/* 1. Today's summary stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Msgs Received Today', val: today.messages_received || 0, icon: '📨' },
          { label: 'Msgs Sent Today', val: today.messages_sent || 0, icon: '📤' },
          { label: 'Fallbacks Today', val: today.fallbacks || 0, icon: '⚠', color: (today.fallbacks || 0) > 0 ? C.orange : C.green },
          { label: 'Est. Cost Today', val: fmtUSD(today.est_cost_usd), icon: '💰' },
        ].map(({ label, val, icon, color }) => (
          <div key={label} data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`} style={{
            padding: '16px 20px', borderRadius: 14, background: C.card, border: `1px solid ${C.border}`,
          }}>
            <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: color || C.text }}>{val}</div>
            <div style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* 2. Per-business usage table */}
      <div style={{ borderRadius: 14, overflow: 'hidden', border: `1px solid ${C.border}`, marginBottom: 20 }}>
        <div style={{
          padding: '12px 16px', background: C.panel, borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Business Usage — This Month</span>
          <button
            data-testid="cost-settings-btn"
            onClick={() => setShowCostPanel(true)}
            style={{
              padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
              background: C.card, border: `1px solid ${C.border}`, color: C.sub,
              cursor: 'pointer',
            }}
          >⚙ Cost Settings</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: C.panel }}>
              <tr>
                <SortHeader field="business_name" label="Business" />
                <SortHeader field="channel" label="Channel" />
                <SortHeader field="messages_received" label="Msgs Recv" />
                <SortHeader field="messages_sent" label="Msgs Sent" />
                <SortHeader field="entries_saved" label="Entries" />
                <SortHeader field="fallback_count" label="Fallbacks" />
                <SortHeader field="avg_latency_ms" label="Avg Latency" />
                <SortHeader field="template_path_pct" label="Template %" />
                <SortHeader field="worker_count" label="Workers" />
                <SortHeader field="est_monthly_cost_usd" label="Est Cost" />
              </tr>
            </thead>
            <tbody>
              {sortedBizs.map((biz, i) => {
                const cost = calcCost(biz);
                const isOverBudget = cost && cost.margin < 0;
                const isExpanded = expandedBiz === biz.business_id;
                return (
                  <Fragment key={biz.business_id}>
                    <tr
                      data-testid={`biz-row-${biz.business_id}`}
                      onClick={() => setExpandedBiz(p => p === biz.business_id ? null : biz.business_id)}
                      style={{
                        borderTop: `1px solid ${C.border}`,
                        background: isOverBudget ? `${C.red}10` : i % 2 === 0 ? 'transparent' : `${C.card}80`,
                        cursor: 'pointer', transition: 'background 0.1s',
                      }}
                    >
                      <td style={{ padding: '10px 12px', color: C.text, fontWeight: 500 }}>
                        <span style={{ display: 'inline-block', width: 14, color: C.sub, fontSize: 10 }}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                        {biz.business_name}
                        {isOverBudget && <span data-testid={`over-budget-flag-${biz.business_id}`} style={{ color: C.red, marginLeft: 6, fontSize: 10, fontWeight: 700 }}>⚠ OVER BUDGET</span>}
                      </td>
                      <td style={{ padding: '10px 12px' }}><ChannelBadge ch={biz.channel} /></td>
                      <td style={{ padding: '10px 12px', color: C.text }}>{biz.messages_received}</td>
                      <td style={{ padding: '10px 12px', color: C.text }}>{biz.messages_sent}</td>
                      <td style={{ padding: '10px 12px', color: C.text }}>{biz.entries_saved}</td>
                      <td style={{ padding: '10px 12px', color: biz.fallback_count > 0 ? C.orange : C.text }}>
                        {biz.fallback_count}
                      </td>
                      <td style={{ padding: '10px 12px', color: biz.avg_latency_ms > 8000 ? C.orange : C.text }}>
                        {fmtMs(biz.avg_latency_ms)}
                      </td>
                      <td style={{ padding: '10px 12px', color: C.text }}>{biz.template_path_pct}%</td>
                      <td style={{ padding: '10px 12px', color: C.text }}>{biz.worker_count}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {cost ? (
                          <div>
                            <div style={{ color: isOverBudget ? C.red : C.text, fontWeight: 700 }}>{fmtUSD(cost.totalUSD)}</div>
                            <div style={{ fontSize: 10, color: C.dim }}>
                              {fmtINR(cost.totalINR)} / {fmtINR(cost.targetINR)}
                              <span style={{
                                marginLeft: 4,
                                color: isOverBudget ? C.red : C.green,
                                fontWeight: 600,
                              }}>
                                ({cost.margin >= 0 ? '+' : ''}{fmtINR(cost.margin)})
                              </span>
                            </div>
                          </div>
                        ) : fmtUSD(biz.est_monthly_cost_usd)}
                      </td>
                    </tr>
                    {isExpanded && cost && (
                      <tr data-testid={`biz-models-${biz.business_id}`} style={{
                        background: C.bg, borderTop: `1px solid ${C.border}`,
                      }}>
                        <td colSpan={10} style={{ padding: '12px 24px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                                AI cost by model
                              </div>
                              {(cost.perModel || []).length === 0 ? (
                                <div style={{ fontSize: 11, color: C.dim }}>No AI usage this month.</div>
                              ) : (cost.perModel.map(m => (
                                <div key={m.model_key} data-testid={`biz-model-${biz.business_id}-${m.model_key}`} style={{
                                  display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0',
                                  borderBottom: `1px solid ${C.border}`,
                                }}>
                                  <span style={{ flex: 1, fontSize: 12, color: C.text, fontWeight: 500 }}>
                                    {m.label}
                                    <span style={{ marginLeft: 6, fontSize: 9, color: C.dim, textTransform: 'uppercase' }}>{m.provider}</span>
                                  </span>
                                  <span style={{ fontSize: 11, color: C.sub, minWidth: 110, textAlign: 'right' }}>
                                    {m.input_tokens.toLocaleString()} in · {m.output_tokens.toLocaleString()} out
                                  </span>
                                  <span style={{ fontSize: 11, color: C.dim, minWidth: 50, textAlign: 'right' }}>
                                    {m.msgs} msgs
                                  </span>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: C.accent, minWidth: 64, textAlign: 'right' }}>
                                    {fmtUSD(m.cost_usd)}
                                  </span>
                                </div>
                              )))}
                            </div>
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                                Cost composition
                              </div>
                              {[
                                { label: 'AI tokens', usd: cost.aiCost },
                                { label: `Messaging (${biz.meta_business_msgs || 0} biz · ${biz.meta_user_msgs || 0} user · ${biz.twilio_msgs || 0} twilio)`, usd: cost.msgCost },
                                { label: 'Infra share', inr: cost.infraPerBizInr },
                              ].map(item => (
                                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.sub }}>
                                  <span>{item.label}</span>
                                  <span style={{ color: C.text, fontWeight: 600 }}>
                                    {item.usd != null ? fmtUSD(item.usd) : fmtINR(item.inr)}
                                  </span>
                                </div>
                              ))}
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 13, fontWeight: 700, color: C.text }}>
                                <span>Total</span>
                                <span>{fmtUSD(cost.totalUSD)} · {fmtINR(cost.totalINR)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.sub }}>
                                <span>Target charge</span>
                                <span>{fmtINR(cost.targetINR)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: cost.margin >= 0 ? C.green : C.red, marginTop: 4 }}>
                                <span>Margin</span>
                                <span>{cost.margin >= 0 ? '+' : ''}{fmtINR(cost.margin)}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {sortedBizs.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: C.dim }}>No data this month</td></tr>
              )}
              {projection && (
                <tr data-testid="projection-row" style={{
                  borderTop: `2px solid ${C.accent}`, background: `${C.accent}08`, fontWeight: 600,
                }}>
                  <td style={{ padding: '12px', color: C.accent, fontWeight: 700 }} colSpan={2}>
                    📈 Projected month-end ({projection.days_elapsed}/{projection.days_in_month} days)
                  </td>
                  <td style={{ padding: '12px', color: C.text }}>{projection.projected_msgs}</td>
                  <td style={{ padding: '12px', color: C.dim }} colSpan={6}>
                    Revenue: <span style={{ color: C.green, fontWeight: 700 }}>{fmtINR(projection.projected_revenue_inr)}</span> ({projection.active_biz_count} biz × {fmtINR(projection.target_charge_inr)}) ·
                    Variable: {fmtINR(projection.projected_total_inr)} · Infra: {fmtINR(projection.projected_infra_inr)}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ color: projection.projected_margin_inr >= 0 ? C.green : C.red, fontWeight: 800 }}>
                      {fmtINR(projection.projected_total_inr)}
                    </div>
                    <div style={{ fontSize: 10, color: projection.projected_margin_inr >= 0 ? C.green : C.red }}>
                      Margin {projection.projected_margin_inr >= 0 ? '+' : ''}{fmtINR(projection.projected_margin_inr)}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 3. AI Extraction Quality widget */}
      <ExtractionQualityWidget data={extractionQuality} />

      {/* 4. Anomalies — last 1 hour only */}
      <div data-testid="anomalies-section" style={{ marginTop: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
          Anomalies (last 1 hour) {anomalies.length > 0 ? `· ${anomalies.length}` : ''}
        </div>
        {anomalies.length === 0 ? (
          <div data-testid="anomalies-all-clear" style={{
            padding: '14px 18px', borderRadius: 10,
            background: `${C.green}12`, border: `1px solid ${C.green}44`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 16 }}>🟢</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>All systems normal</div>
              <div style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>No anomalies detected in the last hour.</div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {visibleAnomalies.map((a, i) => {
                const sc = SEVERITY_COLOR[a.severity] || C.orange;
                return (
                  <div key={i} data-testid={`anomaly-${a.type}`} style={{
                    padding: '10px 14px', borderRadius: 10,
                    background: `${sc}12`, border: `1px solid ${sc}44`,
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <span style={{ fontSize: 14 }}>{a.severity === 'critical' ? '🔴' : '🟡'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: sc }}>{a.business_name} — {a.type.replace(/_/g, ' ')}</div>
                      <div style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>{a.description}</div>
                    </div>
                    <div style={{ fontSize: 10, color: C.dim }}>{relTime(a.timestamp)}</div>
                  </div>
                );
              })}
            </div>
            {anomalies.length > 5 && (
              <button
                data-testid="show-more-anomalies-btn"
                onClick={() => setShowAllAnomalies(p => !p)}
                style={{
                  marginTop: 8, padding: '5px 14px', borderRadius: 8, fontSize: 11,
                  fontWeight: 600, background: 'transparent', border: `1px solid ${C.border}`,
                  color: C.sub, cursor: 'pointer',
                }}
              >{showAllAnomalies ? 'Show less' : `Show all ${anomalies.length} anomalies`}</button>
            )}
          </>
        )}
      </div>

      {/* Cost settings slide panel */}
      {showCostPanel && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100,
          display: 'flex', justifyContent: 'flex-end',
        }} onClick={() => setShowCostPanel(false)}>
          <div data-testid="cost-settings-panel" onClick={e => e.stopPropagation()} style={{
            width: 480, height: '100%', background: C.panel, borderLeft: `1px solid ${C.border}`,
            padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Cost Settings</div>
              <button onClick={() => setShowCostPanel(false)} style={{ background: 'none', border: 'none', color: C.sub, fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>

            {/* ─── Per-model AI pricing ──────────────────────────────────── */}
            <ModelPricingSection costForm={costForm} setCostForm={setCostForm} />

            {/* ─── Messaging ─────────────────────────────────────────────── */}
            <PanelSection title="Messaging" color={C.twilio}>
              {[
                { key: 'meta_business_initiated_cost', label: 'Meta — business-initiated ($/conv)' },
                { key: 'meta_user_initiated_cost',     label: 'Meta — user-initiated ($/conv)' },
                { key: 'twilio_per_msg',               label: 'Twilio — per message ($)' },
              ].map(({ key, label }) => (
                <NumberRow key={key} keyName={key} label={label} costForm={costForm} setCostForm={setCostForm} step="0.0001" />
              ))}
            </PanelSection>

            {/* ─── Infrastructure ────────────────────────────────────────── */}
            <PanelSection title="Infrastructure (₹/month)" color={C.web}>
              {[
                { key: 'supabase_fixed_inr', label: 'Supabase' },
                { key: 'railway_fixed_inr',  label: 'Railway' },
                { key: 'redis_fixed_inr',    label: 'Redis' },
              ].map(({ key, label }) => (
                <NumberRow key={key} keyName={key} label={label} costForm={costForm} setCostForm={setCostForm} step="50" />
              ))}
              {/* Auto-calculated: total infra and per-message */}
              <InfraTotals costForm={costForm} infra={infra} />
            </PanelSection>

            {/* ─── Pricing ───────────────────────────────────────────────── */}
            <PanelSection title="Pricing" color={C.green}>
              <NumberRow keyName="target_charge_per_biz_inr" label="Target charge / business (₹/month)" costForm={costForm} setCostForm={setCostForm} step="100" />
              <NumberRow keyName="usd_to_inr" label="USD → INR exchange rate" costForm={costForm} setCostForm={setCostForm} step="0.5" />
            </PanelSection>

            {/* ─── Anomaly thresholds ───────────────────────────────────── */}
            <PanelSection title="Anomaly Detection" color={C.orange}>
              {[
                { key: 'slow_response_threshold_ms', label: 'Slow response threshold (ms)', step: '500' },
                { key: 'fallback_rate_threshold',    label: 'Fallback rate threshold (per hour)', step: '1' },
              ].map(({ key, label, step }) => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, color: C.sub, display: 'block', marginBottom: 4 }}>{label}</label>
                  <input
                    data-testid={`threshold-${key}`}
                    type="number" step={step}
                    value={costForm[key] ?? ''}
                    onChange={e => setCostForm(p => ({ ...p, [key]: parseInt(e.target.value) || 0 }))}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13,
                      background: C.card, border: `1px solid ${C.orange}44`, color: C.text,
                      outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>
              ))}
            </PanelSection>

            <button
              data-testid="save-cost-settings-btn"
              onClick={saveCostSettings}
              style={{
                marginTop: 8, padding: '12px', borderRadius: 12, fontSize: 14, fontWeight: 700,
                background: 'linear-gradient(135deg,#FF2D55,#5856D6)', color: '#fff',
                border: 'none', cursor: 'pointer',
              }}
            >Save Settings</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN ADMIN DASHBOARD ─────────────────────────────────────────────────────
export default function AdminDashboardPage() {
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem('admin_token') || '');
  const [authenticated, setAuthenticated] = useState(!!sessionStorage.getItem('admin_token'));
  const [currentPage, setCurrentPage] = useState('monitor');
  const [businesses, setBusinesses] = useState([]);
  // Theme: read from localStorage so it persists across refreshes.
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = window.localStorage.getItem('admin_theme_mode');
      if (saved === 'light' || saved === 'dark') {
        _setThemeMode(saved);
        return saved;
      }
    }
    return 'dark';
  });

  const toggleTheme = useCallback(() => {
    setThemeMode(m => {
      const next = m === 'dark' ? 'light' : 'dark';
      // Apply to module-level Proxy reference SYNCHRONOUSLY so the cascading
      // re-render triggered by setState reads the new palette on first paint.
      _setThemeMode(next);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('admin_theme_mode', next);
      }
      return next;
    });
  }, []);

  const handleAuth = useCallback((token) => {
    setAdminToken(token);
    setAuthenticated(true);
  }, []);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('admin_token');
    setAuthenticated(false);
    setAdminToken('');
  }, []);

  // Load businesses on auth
  useEffect(() => {
    if (!authenticated || !adminToken) return;
    fetch(`${API}/admin/businesses`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(r => r.json())
      .then(d => setBusinesses(d.businesses || []))
      .catch(() => {});
  }, [authenticated, adminToken]);

  if (!authenticated) {
    return <LoginScreen onAuth={handleAuth} />;
  }

  return (
    <div data-testid="admin-dashboard" style={{
      height: '100vh', overflow: 'hidden', background: C.bg, display: 'flex', flexDirection: 'column',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
    }}>
      {/* Top navigation */}
      <header data-testid="admin-top-nav" style={{
        height: 52, flexShrink: 0, display: 'flex', alignItems: 'center',
        padding: '0 20px', gap: 12,
        background: C.panel, borderBottom: `1px solid ${C.border}`,
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.03em', color: C.text, marginRight: 8 }}>
          NOTE<span style={{ background: 'linear-gradient(135deg,#FF2D55,#5856D6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>HO</span>
          <span style={{ fontSize: 11, color: C.dim, fontWeight: 500, marginLeft: 8 }}>ADMIN</span>
        </div>

        {/* Nav tabs */}
        <div style={{ display: 'flex', gap: 4, background: C.card, borderRadius: 10, padding: 4 }}>
          {[
            { id: 'monitor', label: 'Conversation Monitor' },
            { id: 'usage', label: 'Usage & Cost' },
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`nav-tab-${tab.id}`}
              onClick={() => setCurrentPage(tab.id)}
              style={{
                padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: currentPage === tab.id ? 'linear-gradient(135deg,#FF2D55,#5856D6)' : 'transparent',
                color: currentPage === tab.id ? '#fff' : C.sub, border: 'none', transition: 'all 0.15s',
              }}
            >{tab.label}</button>
          ))}
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: C.dim }}>{businesses.length} businesses</span>
          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            title={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              padding: '5px 10px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: 'transparent', border: `1px solid ${C.border}`, color: C.sub,
              cursor: 'pointer', minWidth: 36, lineHeight: '1',
            }}
          >{themeMode === 'dark' ? '☀' : '☾'}</button>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            style={{
              padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
              background: 'transparent', border: `1px solid ${C.border}`, color: C.sub, cursor: 'pointer',
            }}
          >Sign Out</button>
        </div>
      </header>

      {/* Page content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {currentPage === 'monitor' ? (
          <ConversationMonitor adminToken={adminToken} businesses={businesses} />
        ) : (
          <UsageDashboard
            adminToken={adminToken}
            onDrillDown={(bizId) => {
              setCurrentPage('monitor');
            }}
          />
        )}
      </div>
    </div>
  );
}
